import { Typography, Skeleton } from '@mui/material'
import { APIError } from 'components/core/errors/api'
import { InsightItem } from 'components/core/insights'
import { userStatusToIntl } from 'components/pages/user/type'
import { SearchAppsRequest } from 'pbts/c1/api/app/v1/app'
import { InsightType } from 'pbts/c1/api/task/v1/insights'
import { User, UserStatus } from 'pbts/c1/api/user/v1/user'
import React, { FC } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { appSearchNonExpandedApi } from 'redux_toolkit/apps'

type StatusLabelProps = {
    userStatus: UserStatus
    user?: User
}

export const StatusLabel: FC<StatusLabelProps> = ({ userStatus, user }) => {
    const intl = useIntl()
    if (!userStatus) {
        return null
    }
    const actionable = user?.directoryIds && user?.directoryIds.length > 0
    const {
        data,
        isLoading,
        isError,
        error,
    } = appSearchNonExpandedApi.useSearch(SearchAppsRequest.create({ appIds: user?.directoryIds }), { actionable: !!actionable })

    if (isLoading && actionable) {
        return <Skeleton variant="text" />
    }
    if (isError) {
        return <APIError err={error} />
    }
    const status = userStatusToIntl(userStatus, intl).toLowerCase()
    switch (userStatus) {
        case UserStatus.ENABLED:
        case UserStatus.DISABLED:
        case UserStatus.DELETED:
            return (
                <Typography>
                    <FormattedMessage
                        id="components_insights_status"
                        defaultMessage="{user} has <b>{user_status}</b> account status in {directory}"
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            user: user?.displayName || intl.formatMessage({ id: 'user_status_unknown_user', defaultMessage: 'This user' }),
                            user_status: status,
                            directory: data && data.list.length > 0 ? data.list.map((directory) => directory.displayName).join(', ') : intl.formatMessage({ id: 'user_status_unknown_directory', defaultMessage: 'the directory' }),
                        }}
                    />
                </Typography>
            )
        default:
            return null
    }
}

type StatusProps = {
    userStatus: UserStatus
    insightType: InsightType
    user?: User
}

export const canRenderStatus = (userStatus: UserStatus): boolean => {
    switch (userStatus) {
        case UserStatus.ENABLED:
        case UserStatus.DISABLED:
        case UserStatus.DELETED:
            return true
        default:
            return false
    }
}

export const Status: FC<StatusProps> = ({ userStatus, insightType, user }) => {
    if (!canRenderStatus(userStatus)) {
        return null
    }
    return (
        <InsightItem key="user-status" insightType={insightType}>
            <StatusLabel userStatus={userStatus} user={user} />
        </InsightItem>
    )
}
